import type React from 'react';
import type { ErrorResponse } from 'react-router';
import { isRouteErrorResponse, Link, useLocation, useParams, useRouteError } from 'react-router';

import { cn, getErrorMessage } from '@april9au/react-common/utils';

import { Button } from '@/components/ui/button';

type StatusHandler = (info: {
  error: ErrorResponse;
  params: Record<string, string | undefined>;
  description?: string;
}) => React.JSX.Element | null;
type CustomStatusHandler = (info: {
  error: ErrorResponse;
  params: Record<string, string | undefined>;
}) => string | null;

export function GeneralErrorBoundary({
  defaultStatusHandler = DefaultErrorHandler,
  statusHandlers,
  unexpectedErrorHandler = UnexpectedErrorHandler,
  className,
}: {
  defaultStatusHandler?: StatusHandler;
  statusHandlers?: Record<number, CustomStatusHandler>;
  unexpectedErrorHandler?: (error: unknown) => React.JSX.Element | null;
  className?: string;
}) {
  const error = useRouteError();
  const params = useParams();

  return (
    <div className={cn('flex h-full min-h-screen items-center bg-gray-100 sm:px-8', className)}>
      <div className="mx-auto w-full max-w-7xl lg:px-8">
        <div className="relative px-4 sm:px-8 lg:px-12">
          <div className="mx-auto max-w-2xl lg:max-w-5xl">
            {isRouteErrorResponse(error)
              ? defaultStatusHandler({
                error,
                params,
                description: statusHandlers?.[error.status]?.({ error, params }) ?? undefined,
              })
              : unexpectedErrorHandler(error)}
          </div>
        </div>
      </div>
    </div>
  );
}

const DEFAULT_ERROR_DESCRIPTION = 'An unexpected error occurred. Please try again';

function DefaultErrorHandler({
  error,
  description = DEFAULT_ERROR_DESCRIPTION,
}: {
  error: ErrorResponse;
  description?: string;
}) {
  const location = useLocation();

  let title = 'An error occurred';

  switch (error.status) {
    case 404: {
      title = 'Page not found';
      if (description === DEFAULT_ERROR_DESCRIPTION) {
        description = "Sorry, we couldn't find this page";
      }
      break;
    }
    case 403: {
      title = 'Unauthorized';
      if (description === DEFAULT_ERROR_DESCRIPTION) {
        description = 'You are not allowed to do that';
      }
      break;
    }
    case 500: {
      title = 'Server error';
      if (description === DEFAULT_ERROR_DESCRIPTION) {
        description = 'An unexpected server error occurred. Please try again';
      }
      break;
    }
  }

  return (
    <div className="flex flex-col items-center">
      <p className="text-muted-foreground/70 text-base font-semibold">{error.status}</p>
      <h1 className="text-primary mt-4 text-4xl font-bold tracking-tight sm:text-5xl">{title}</h1>

      <div className="flex flex-col items-center gap-3">
        <p className="text-muted-foreground mt-2 max-w-sm text-center text-base">{description}</p>

        {error.status === 404 && (
          <pre className="border-primary/10 bg-primary/[2.5%] text-primary rounded border px-1.5 py-0.5 text-base font-medium break-all whitespace-pre-wrap sm:text-xs">
            {location.pathname}
          </pre>
        )}
      </div>

      {error.status === 500 ? (
        <Button onClick={() => window.location.reload()} className="mt-8">
          Reload the page
        </Button>
      ) : (
        <Button variant="outline" className="mt-8" asChild>
          <Link to="/">Go back home</Link>
        </Button>
      )}
    </div>
  );
}

function UnexpectedErrorHandler(error: unknown) {
  if (
    (typeof error === 'object' &&
      error !== null &&
      'message' in error &&
      typeof error.message === 'string' &&
      error.message === 'Failed to fetch') ||
    (typeof navigator !== 'undefined' && !navigator?.onLine)
  ) {
    return (
      <div className="flex flex-col items-center">
        <p className="text-muted-foreground/70 text-base font-semibold">Connection error</p>
        <h1 className="text-primary mt-2 text-4xl font-bold tracking-tight sm:text-5xl">Offline</h1>

        <div className="flex flex-col items-center gap-3">
          <p className="text-muted-foreground mt-4 max-w-sm text-center text-base">
            It seems you're offline. Please check your internet connection and try again
          </p>
        </div>
        <Button onClick={() => window.location.reload()} className="mt-8">
          Reload the page
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center">
      <p className="text-muted-foreground/70 text-base font-semibold">Runtime</p>
      <h1 className="text-primary mt-2 text-4xl font-bold tracking-tight sm:text-5xl">An error occurred</h1>

      <div className="flex flex-col items-center gap-3">
        <p className="text-muted-foreground mt-4 max-w-sm text-center text-base">
          An unexpected error occurred and the developers have been notified. Please try again
        </p>

        <pre className="border-primary/10 bg-primary/[2.5%] text-primary rounded border px-1.5 py-0.5 text-base font-medium break-all whitespace-pre-wrap sm:text-xs">
          {getErrorMessage(error)}
        </pre>
      </div>
    </div>
  );
}
